import type { Maybe } from '@seaber/common/graphql/types';
import { LoadingUnit } from '@seaber/common/graphql/types';
import { CUBIC_METER_IN_CUBIC_FEET } from '../../constants/VolumeUnits';
import { exhaustiveCheck } from '@seaber/common/lib/generics';
import type { ScenarioOrder } from '../../lib/planningData';
import type { ScheduleDataContext } from './ScheduleDataContext';
import { stringToNumOpt } from './util';
import type {
  CoaEvent,
  LoadingRateSource,
  OffHireEvent,
  PortCallEvent,
  SingleEvent,
  SpotEvent,
  StaticEvent,
  TCOrderEvent,
} from './TimeData';
import type {
  SpotEntry,
  TCOrderEntry,
  CoaEntry,
} from '@seaber/common/core/entries';
import { isDischargeEvent, isLoadEvent } from './TimeData';
import { minimumEventDurationHours } from './Core';

export function ensureLoadBeforeDischarge(
  vesselEvents: (
    | TCOrderEvent
    | SingleEvent
    | OffHireEvent
    | PortCallEvent
    | SpotEvent
    | CoaEvent
    | StaticEvent
  )[],
) {
  const orderToLoadIndex = new Map<ScenarioOrder, number>();
  const l = vesselEvents.length;
  for (let i = 0; i < l; i++) {
    const event = vesselEvents[i];
    if (isLoadEvent(event)) {
      orderToLoadIndex.set(event.order, i);
    }
  }
  // Need to process in reverse for comparison with orderToLoadIndex to be valid
  for (let i = l - 1; i >= 0; i--) {
    const event = vesselEvents[i];
    if (isDischargeEvent(event)) {
      const loadIndex = orderToLoadIndex.get(event.order);
      if (loadIndex === undefined) {
        throw new Error('Strange data');
      }
      if (i - loadIndex < 1) {
        // Indices change when splicing, need to find current
        for (let currIndex = loadIndex; currIndex < l; currIndex++) {
          const e = vesselEvents[currIndex];
          if (isLoadEvent(e) && e.order === event.order) {
            vesselEvents.splice(currIndex, 1);
            vesselEvents.splice(i, 0, e);
            break;
          }
        }
      }
    }
  }
}

export const dischargeTimesAfterLoadTimes = <
  T extends { start: number; startOverride: Maybe<number> },
>(
  load: T,
  discharge: T,
): void => {
  discharge.start = Math.max(
    discharge.start,
    load.start + 1,
    load.startOverride ?? 1,
  );
  if (discharge.startOverride) {
    discharge.startOverride = Math.max(
      discharge.startOverride,
      load.start + 1,
      load.startOverride ?? 1,
    );
  }
};

/**
 * Calculates time to load or unload a single event in hours
 * @param m mass in tonnes
 * @param v volume in m3
 * @param loadingRate
 * @param loadingUnit
 * @param overhead overhead time in hours
 */
export function calcLoadTime(
  m: number,
  v: number,
  loadingRate: number,
  loadingUnit: LoadingUnit | null | undefined,
  overhead: number | null,
): number {
  switch (loadingUnit) {
    case LoadingUnit.CUBIC_FEET_PER_DAY: {
      return (
        (CUBIC_METER_IN_CUBIC_FEET * 24 * v) / loadingRate + (overhead ?? 0)
      );
    }
    case LoadingUnit.CUBIC_METERS_PER_DAY:
    case LoadingUnit.SCBMUB_DAY:
    case LoadingUnit.SCBMOB_DAY: {
      return (24 * v) / loadingRate + (overhead ?? 0);
    }
    case LoadingUnit.LOADING_IN_HOURS_FOR_THE_WHOLE_PARCEL: {
      return +loadingRate + (overhead ?? 0);
    }
    case LoadingUnit.LOADING_IN_DAYS_FOR_THE_WHOLE_PARCEL: {
      return +loadingRate * 24 + (overhead ?? 0);
    }
    case LoadingUnit.METRIC_TONNES_PER_DAY:
    case LoadingUnit.BDMT_DAY:
    case LoadingUnit.ADMT_DAY: {
      return (24 * m) / loadingRate + (overhead ?? 0);
    }
    case LoadingUnit.METRIC_TONNES_PER_HOUR:
    case LoadingUnit.BDMT_HOUR:
    case LoadingUnit.ADMT_HOUR: {
      return m / loadingRate + (overhead ?? 0);
    }
    case LoadingUnit.CUBIC_METERS_PER_HOUR:
    case LoadingUnit.SCBMUB_HOUR:
    case LoadingUnit.SCBMOB_HOUR: {
      return v / loadingRate + (overhead ?? 0);
    }
    case LoadingUnit.CUBIC_FEET_PER_HOUR: {
      return (CUBIC_METER_IN_CUBIC_FEET * v) / loadingRate + (overhead ?? 0);
    }
    case null:
    case undefined: {
      return overhead ?? 0;
    }
    default: {
      exhaustiveCheck(loadingUnit);
    }
  }
}

export function getOrderContractLoadingRates(
  operation: 'loading' | 'discharge',
  order: ScenarioOrder,
  coas: ScheduleDataContext['coasById'],
  entry?: TCOrderEntry | SpotEntry | CoaEntry,
): {
  rate: number | null;
  unit: LoadingUnit | null;
  source: 'voyage_charter' | 'coa' | null;
} {
  let rate: number | null = null;
  let unit: LoadingUnit | null = null;

  const voyageCharter =
    order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId ?? null;

  const locationId =
    operation === 'loading' ? order.loadPortId : order.dischargePortId;

  if (!locationId) {
    return {
      source: null,
      rate,
      unit,
    };
  }

  const vcPortData =
    voyageCharter?.voyageCharterPortDataByVoyageCharterId.nodes.find(
      (pd) => pd?.portDatumByPortDataId?.portId === locationId,
    )?.portDatumByPortDataId;

  const rateProperty =
    operation === 'loading' ? 'loadingRate' : 'dischargeRate';

  rate = stringToNumOpt(vcPortData?.[rateProperty]) ?? null;
  unit = vcPortData?.loadingDischargeUnit ?? null;

  if (rate) {
    return {
      source: 'voyage_charter',
      rate,
      unit,
    };
  }

  const coaId =
    (entry && 'coaId' in entry ? entry.coaId : null) ??
    voyageCharter?.voyageCharterToContractsByVoyageCharterId.nodes.at(0)
      ?.coaId ??
    order.coaId;

  if (!coaId) {
    return {
      source: null,
      rate,
      unit,
    };
  }

  const coa = coas.get(coaId);
  const coaPortData = coa?.portDataRelations.nodes.find(
    (pd) => pd?.portData?.portId === locationId,
  )?.portData;

  rate = stringToNumOpt(coaPortData?.[rateProperty]) ?? null;
  unit = coaPortData?.loadingDischargeUnit ?? null;

  return {
    source: rate ? 'coa' : null,
    rate,
    unit,
  };
}

function isValidLoadingHours(hours: number) {
  return isFinite(hours) && hours >= minimumEventDurationHours;
}

export type RateInfo = {
  rate?: number | null;
  unit?: LoadingUnit | null;
} | null;

export type RateSourceKey =
  | 'order'
  | 'vessel'
  | 'port'
  | 'voyageCharter'
  | 'coa'
  | 'assortment'
  | 'portCommodity';

export type GetEffectiveLoadingRateParams = Record<RateSourceKey, RateInfo>;

export function isValidRate(
  loadingRate?: GetEffectiveLoadingRateParams[keyof GetEffectiveLoadingRateParams],
): loadingRate is { rate: number; unit: LoadingUnit } {
  return !!(
    loadingRate &&
    loadingRate.rate !== null &&
    loadingRate.unit !== null
  );
}

export type EffectiveLoadingRate = {
  source: LoadingRateSource;
  rate: number;
  unit: LoadingUnit;
};

/** Loading rate source in order of preference (most to least preferable) */
const LOADING_RATE_PREFERENCES: Array<keyof GetEffectiveLoadingRateParams> = [
  'order', // Cargo Details
  'voyageCharter', // Laytime terms
  'coa', // COA contract
  'portCommodity', // Port - Commodity LD rate
  'vessel', // Vessel
  'assortment', // Commodity
  'port', // fallback
];

/** Maps parameter keys to source values in the result */
const PARAM_KEY_TO_SOURCE: Record<
  keyof GetEffectiveLoadingRateParams,
  LoadingRateSource
> = {
  order: 'order',
  voyageCharter: 'voyage_charter',
  coa: 'coa',
  assortment: 'assortment',
  vessel: 'vessel',
  port: 'port',
  portCommodity: 'port_commodity',
};

/** Determines which one of the available loading/discharge rates should be used */
export function getEffectiveLoadingRate(rates: GetEffectiveLoadingRateParams):
  | EffectiveLoadingRate
  | {
      source: null;
      rate: null;
      unit: null;
    } {
  // Try each rate source in order of preference
  for (const paramKey of LOADING_RATE_PREFERENCES) {
    const rateData = rates[paramKey];
    if (isValidRate(rateData)) {
      return {
        source: PARAM_KEY_TO_SOURCE[paramKey],
        ...rateData,
      };
    }
  }

  return {
    source: null,
    rate: null,
    unit: null,
  };
}

/** Calculates time to load or unload a single event in hours */
export function getLoadingTime(
  m: number, // mass
  v: number, // volume
  loadingRate: number | null,
  loadingUnit: LoadingUnit | null,
  overheadDurationHours?: number | null,
): number {
  if (loadingRate) {
    const result = calcLoadTime(
      m,
      v,
      loadingRate,
      loadingUnit,
      overheadDurationHours ?? null,
    );

    if (isValidLoadingHours(result)) return result;
  }

  if (overheadDurationHours) {
    const result = calcLoadTime(
      m,
      v,
      Infinity,
      loadingUnit,
      overheadDurationHours,
    );

    if (isValidLoadingHours(result)) return result;
  }

  return minimumEventDurationHours;
}
